import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { marked } from "marked";
import { getBlogIndex, parseFrontmatter } from "./blogData";
import { useDocumentMeta } from "../seo/useDocumentMeta";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPost, setRelatedPost] = useState(null);
  const [status, setStatus] = useState("loading");
  useDocumentMeta(
    post
      ? {
          title: `${post.title} | LooporaData`,
          description: post.summary || post.title,
          path: `/blog/${post.slug}`,
        }
      : null
  );

  useEffect(() => {
    let isMounted = true;

    async function loadPost() {
      try {
        const posts = await getBlogIndex();
        const selected = posts.find((item) => item.slug === slug);
        if (!selected) {
          if (isMounted) setStatus("not-found");
          return;
        }
        const firstRelated = posts.find((item) => item.slug !== slug) || null;

        const response = await fetch(
          `${process.env.PUBLIC_URL || ""}/${selected.file}`
        );
        if (!response.ok) {
          if (isMounted) setStatus("error");
          return;
        }

        const markdown = await response.text();
        const { body, frontmatter } = parseFrontmatter(markdown);
        const cleanedBody = stripLeadingTitleHeading(
          body,
          frontmatter.title || selected.title
        );

        if (!isMounted) return;
        setPost({
          ...selected,
          ...frontmatter,
          html: marked.parse(cleanedBody),
        });
        setRelatedPost(firstRelated);
        setStatus("ready");
      } catch (error) {
        if (isMounted) setStatus("error");
      }
    }

    loadPost();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <main className="main" id="top">
      <section className="section section--muted section--with-glow" aria-label="Blog post">
        <div className="ambient-glow ambient-glow--solution-1" aria-hidden="true" />
        <div className="ambient-glow ambient-glow--solution-2" aria-hidden="true" />
        <div className="container content-with-sidebar">
          {status === "loading" && <p>Loading post...</p>}
          {status === "error" && <p>We could not load this post right now.</p>}
          {status === "not-found" && <p>Post not found.</p>}

          {status === "ready" && post && (
            <article className="content-page">
              <header>
                <p className="section__label">
                  <Link className="link" to="/blog">
                    Blog
                  </Link>
                  {" > "}
                  {formatDate(post.date)}
                  {post.author ? ` · ${post.author}` : ""}
                </p>
                <h1 className="section__headline">{post.title}</h1>
                {post.summary && <p className="section__intro">{post.summary}</p>}
                {Array.isArray(post.tags) && post.tags.length > 0 && (
                  <div className="blog-tag-list" aria-label="Post tags">
                    {post.tags.map((tag) => (
                      <span className="blog-tag-chip" key={`${post.slug}-${tag}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </header>
              <div
                dangerouslySetInnerHTML={{ __html: toTempDesignHtml(post.html) }}
              />
            </article>
          )}
          {status === "ready" && (
            <aside className="content-sidebar" aria-label="Related post">
              <div className="cards cards--alt-neutral content-sidebar__cards">
                <article className="card">
                  <div className="card__stepHeader">
                    <div>
                      <h3 className="card__title card__title--step">Related Post</h3>
                      <p className="card__summary">
                        {relatedPost ? relatedPost.title : "More posts coming soon"}
                      </p>
                    </div>
                  </div>
                  {relatedPost?.summary ? (
                    <p className="card__body">{relatedPost.summary}</p>
                  ) : (
                    <p className="card__body">
                      We are publishing new updates soon. Check the blog index for the
                      latest posts.
                    </p>
                  )}
                  {relatedPost ? (
                    <p className="card__body">
                      <Link className="link" to={`/blog/${relatedPost.slug}`}>
                        Read post
                      </Link>
                    </p>
                  ) : (
                    <p className="card__body">
                      <Link className="link" to="/blog">
                        View all posts
                      </Link>
                    </p>
                  )}
                </article>
              </div>
            </aside>
          )}
        </div>
      </section>
    </main>
  );
}

function formatDate(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function stripLeadingTitleHeading(markdown, title) {
  const normalizedTitle = (title || "").trim();
  if (!normalizedTitle) return markdown;

  const headingPattern = new RegExp(
    `^\\s*#\\s+${escapeRegExp(normalizedTitle)}\\s*\\n+`,
    "i"
  );
  return markdown.replace(headingPattern, "");
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function toTempDesignHtml(html) {
  return html
    .replace(/<p>/g, '<p class="blog__body">')
    .replace(/<h2>/g, '<h2 class="content-page__h2">')
    .replace(/<ul>/g, '<ul class="content-page__list">')
    .replace(/<ol>/g, '<ol class="content-page__list">');
}
